import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';

// The right-hand column of AppLayout (from `xl`). A page renders <ContextPanel> where its side content belongs on
// narrow screens; from `xl` the same content moves into the column next to the page. Pages without a panel get no
// column, so the main area keeps the full width.

/** Tailwind's `xl` breakpoint */
const WIDE_QUERY = '(min-width: 80rem)';

/** True while the media query matches; false on the server and in tests without matchMedia */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false),
    () => false,
  );
}

interface ContextPanelSlot {
  target: HTMLElement | null;
  wide: boolean;
  /** Called by each mounted ContextPanel; returns the cleanup */
  register: () => () => void;
}

const SlotContext = createContext<ContextPanelSlot | null>(null);

/** Used by AppLayout: tracks whether a page has a panel and where to render it. */
export function useContextPanelSlot() {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [count, setCount] = useState(0);
  const wide = useMediaQuery(WIDE_QUERY);
  const register = useCallback(() => {
    setCount((n) => n + 1);
    return () => setCount((n) => n - 1);
  }, []);
  const slot = useMemo<ContextPanelSlot>(() => ({ target, wide, register }), [target, wide, register]);
  return { slot, setTarget, hasPanel: count > 0, wide };
}

export function ContextPanelProvider({ slot, children }: { slot: ContextPanelSlot; children: ReactNode }) {
  return <SlotContext.Provider value={slot}>{children}</SlotContext.Provider>;
}

interface ContextPanelProps {
  children: ReactNode;
  /** Classes for the inline version shown below `xl` */
  className?: string;
}

/**
 * Side content for the current page: details of the selected item, the related job, profile or to-dos.
 * Next to the page from `xl`, in place below that. Outside AppLayout it always renders in place.
 */
export function ContextPanel({ children, className }: ContextPanelProps) {
  const slot = useContext(SlotContext);
  const register = slot?.register;
  useEffect(() => register?.(), [register]);

  if (slot?.wide && slot.target) return createPortal(children, slot.target);
  return <div className={cn('grid gap-3', className)}>{children}</div>;
}
